import React, { lazy, Suspense, useEffect } from 'react';
import { Routes, Route, Navigate, useNavigate, useLocation, useParams } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { SnackbarProvider, MaterialDesignContent, closeSnackbar } from 'notistack';
import { styled } from '@mui/material/styles';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import { LanguageProvider } from './context/LanguageContext';
import { HelmetProvider } from 'react-helmet-async';
import './i18n';
import './styles/print.css';
import './styles/rtl.css';

// Core layout & auth
import Layout from './components/Layout/Layout';
import ProtectedRoute from './components/auth/ProtectedRoute';
import ErrorBoundary from './components/common/ErrorBoundary';
import LoadingSpinner from './components/common/LoadingSpinner';
import CookieBanner from './components/common/CookieBanner';
import ScrollToTop from './components/common/ScrollToTop';
import AIChatWidget from './components/shared/AIChatWidget';
import CommandPalette from './components/shared/CommandPalette';
import PartnerProtectedRoute from './components/auth/PartnerProtectedRoute';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ThemeModeProvider, useThemeMode } from './context/ThemeContext';
import { getThemeOptions } from './theme';
import { RegionProvider } from './context/RegionContext';
import { SubscriptionRestrictionProvider } from './context/SubscriptionRestrictionContext';
import { FeatureFlagProvider } from './context/FeatureFlagContext';
import { CurrencyProvider } from './context/CurrencyContext';
import SessionExpiredDialog from './components/auth/SessionExpiredDialog';
import { posRoutes, CustomerDisplayScreen } from './pages/POS/routes';
import { reportsRoutes } from './pages/Reports/routes';
import { adminRoutes } from './pages/Admin/routes';
import { documentRoutes } from './pages/DocumentPlatform/routes';
import { salesRoutes } from './pages/Sales/routes';
import { crmRoutes } from './pages/CRM/routes';
import { procurementRoutes } from './pages/Procurement/routes';
import { inventoryRoutes } from './pages/Inventory/routes';
import { manufacturingRoutes } from './pages/Manufacturing/routes';
import { tradeRoutes } from './pages/Trade/routes';
import { accountingRoutes } from './pages/Accounting/routes';
import { budgetingRoutes } from './pages/Budgeting/routes';
import { hrRoutes } from './pages/HR/routes';
import { projectsRoutes } from './pages/Projects/routes';
import { aiRoutes } from './pages/AI/routes';
import { complianceRoutes } from './pages/Compliance/routes';
import { eamRoutes } from './pages/EAM/routes';
import { settingsRoutes } from './pages/Settings/routes';

import {
  HomePage,
  FeaturesPage,
  PricingPage,
  AboutPage,
  FAQsPage,
  ContactPage,
  RequestDemoPage,
  PrivacyPolicyPage,
  TermsOfServicePage,
  SecurityPage,
  ApiDocumentationPage,
  BecomePartnerPage,
  CookiePolicyPage,
  DPAPage,
  BlogsPage,
  BlogDetailPage,
  StatusPage,
  SEOLandingPage,
  landingPagesConfig
} from './pages/Website';
// Auth pages
const Login = lazy(() => import('./pages/Auth/Login'));
const Register = lazy(() => import('./pages/Auth/Register'));
const StepwiseRegister = lazy(() => import('./pages/Auth/StepwiseRegister'));
const ForgotPassword = lazy(() => import('./pages/Auth/ForgotPassword'));
const PasswordExpired = lazy(() => import('./pages/Auth/PasswordExpired'));
const Forbidden = lazy(() => import('./pages/Forbidden'));

// Dashboard
const Dashboard = lazy(() => import('./pages/Dashboard/Dashboard'));


const DocumentVerification = lazy(() => import('./pages/Admin/DocumentVerification'));



// Document Platform
const Designer = lazy(() => import('./pages/DocumentPlatform/Designer'));



// Partner Panel pages
const PartnerLogin = lazy(() => import('./pages/PartnerPanel/PartnerLogin'));
const PartnerLayout = lazy(() => import('./pages/PartnerPanel/PartnerLayout'));
const PartnerDashboard = lazy(() => import('./pages/PartnerPanel/PartnerDashboard'));
const PartnerNetwork = lazy(() => import('./pages/PartnerPanel/PartnerNetwork'));
const PartnerReferrals = lazy(() => import('./pages/PartnerPanel/PartnerReferrals'));
const PartnerCommissions = lazy(() => import('./pages/PartnerPanel/PartnerCommissions'));
const PartnerWithdrawals = lazy(() => import('./pages/PartnerPanel/PartnerWithdrawals'));
const PartnerProfile = lazy(() => import('./pages/PartnerPanel/PartnerProfile'));
const PartnerDeals = lazy(() => import('./pages/PartnerPanel/PartnerDeals'));
const PartnerLeadRegistrations = lazy(() => import('./pages/PartnerPanel/PartnerLeadRegistrations'));
const PartnerHelpdesk = lazy(() => import('./pages/PartnerPanel/PartnerHelpdesk'));
const PartnerTicketDetail = lazy(() => import('./pages/PartnerPanel/PartnerTicketDetail'));
const PartnerTargets = lazy(() => import('./pages/PartnerPanel/PartnerTargets'));
const PartnerCertifications = lazy(() => import('./pages/PartnerPanel/PartnerCertifications'));
const PartnerAgreement = lazy(() => import('./pages/PartnerPanel/PartnerAgreement'));
const PartnerOnboarding = lazy(() => import('./pages/PartnerPanel/PartnerOnboarding'));
// PartnerProtectedRoute imported at top



// Contexts imported at top

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
      staleTime: 5 * 60 * 1000,
    },
  },
});

const BlogSlugRedirect = () => {
  const { slug } = useParams();
  return <Navigate to={`/blog/${slug}`} replace />;
};

const ManualRedirect = () => {
  useEffect(() => {
    window.location.replace('/docs/user-manual');
  }, []);
  return <LoadingSpinner />;
};

const AppRoutes = () => {
  const { isAuthenticated, hasPermission, subscriptionStatus } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  // Handle persistent referral tracking
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const ref = searchParams.get('ref');

    if (ref) {
      // Store the referral code persistently
      localStorage.setItem('partnerReferralCode', ref);

      // Clean up the URL by removing the ref parameter
      searchParams.delete('ref');
      const newSearch = searchParams.toString();
      const newUrl = location.pathname + (newSearch ? `?${newSearch}` : '') + location.hash;

      // Replace the current history state to keep URLs clean
      navigate(newUrl, { replace: true });
    }
  }, [location, navigate]);

  const query = new URLSearchParams(location.search);
  const isUpgradeFlow = query.get('upgrade') === 'true';
  const isExpired = subscriptionStatus?.trialExpired || subscriptionStatus?.subscriptionExpired;
  const canAccessStepwise = !isAuthenticated || isUpgradeFlow || isExpired;

  return (
    <>
      <ScrollToTop />
      <CookieBanner />
      <CurrencyProvider>
        <Suspense fallback={<LoadingSpinner />}>
          <Routes>
            {/* Public Website routes */}
            <Route path="/" element={<HomePage />} />
            <Route path="/home" element={<Navigate to="/" replace />} />
            <Route path="/features" element={<FeaturesPage />} />
            <Route path="/pricing" element={<PricingPage />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/faqs" element={<FAQsPage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/demo" element={<RequestDemoPage />} />
            <Route path="/privacy" element={<PrivacyPolicyPage />} />
            <Route path="/terms" element={<TermsOfServicePage />} />
            <Route path="/security" element={<SecurityPage />} />
            <Route path="/api-docs" element={<ApiDocumentationPage />} />
            <Route path="/partner" element={<BecomePartnerPage />} />
            <Route path="/cookie-policy" element={<CookiePolicyPage />} />
            <Route path="/dpa" element={<DPAPage />} />
            <Route path="/blog" element={<BlogsPage />} />
            <Route path="/blog/:slug" element={<BlogDetailPage />} />
            <Route path="/status" element={<StatusPage />} />
            <Route path="/verify" element={<DocumentVerification />} />
            <Route path="/verify/:token" element={<DocumentVerification />} />

            {/* Public Redirects & Legacy paths */}
            <Route path="/manual" element={<ManualRedirect />} />
            <Route path="/user-manual" element={<ManualRedirect />} />
            <Route path="/request-demo" element={<Navigate to="/demo" replace />} />
            <Route path="/blogs" element={<Navigate to="/blog" replace />} />
            <Route path="/blogs/:slug" element={<BlogSlugRedirect />} />

            {/* Dynamically generated SEO Landing Pages */}
            {landingPagesConfig.map((config) => (
              <Route 
                key={config.slug} 
                path={`/${config.slug}`} 
                element={<SEOLandingPage config={config} />} 
              />
            ))}

            {/* Partner Portal Public Routes */}
            <Route path="/partner/login" element={<PartnerLogin />} />

            {/* Partner Portal Protected Routes */}
            <Route element={<PartnerProtectedRoute />}>
              <Route path="/partner/onboarding" element={<PartnerOnboarding />} />
              <Route element={<PartnerLayout />}>
                <Route path="/partner/dashboard" element={<PartnerDashboard />} />
                <Route path="/partner/network" element={<PartnerNetwork />} />
                <Route path="/partner/referrals" element={<PartnerReferrals />} />
                <Route path="/partner/deals" element={<PartnerDeals />} />
                <Route path="/partner/leads" element={<PartnerLeadRegistrations />} />
                <Route path="/partner/helpdesk" element={<PartnerHelpdesk />} />
                <Route path="/partner/helpdesk/:id" element={<PartnerTicketDetail />} />
                <Route path="/partner/targets" element={<PartnerTargets />} />
                <Route path="/partner/certifications" element={<PartnerCertifications />} />
                <Route path="/partner/agreements" element={<PartnerAgreement />} />
                <Route path="/partner/commissions" element={<PartnerCommissions />} />
                <Route path="/partner/withdrawals" element={<PartnerWithdrawals />} />
                <Route path="/partner/profile" element={<PartnerProfile />} />
              </Route>
            </Route>

            {/* Auth pages */}
            <Route path="/login" element={!isAuthenticated ? <Login /> : <Navigate to="/dashboard" replace />} />
            <Route path="/register" element={!isAuthenticated ? <Register /> : <Navigate to="/dashboard" replace />} />
            <Route path="/stepwise-register" element={canAccessStepwise ? <StepwiseRegister /> : <Navigate to="/dashboard" replace />} />
            <Route path="/forgot-password" element={!isAuthenticated ? <ForgotPassword /> : <Navigate to="/dashboard" replace />} />

            {/* Security forced pages — accessible while authenticated */}
            <Route path="/password-expired" element={<PasswordExpired />} />

            {/* Protected routes */}
            <Route element={<ProtectedRoute />}>
              <Route element={<Layout />}>
                {/* ========== CORE ========== */}
                <Route path="/dashboard" element={<Dashboard />} />
                <Route path="/forbidden" element={<Forbidden />} />

                {salesRoutes}
                {crmRoutes}
                {procurementRoutes}
                {inventoryRoutes}
                {manufacturingRoutes}
                {tradeRoutes}
                {accountingRoutes}
                {budgetingRoutes}
                {hrRoutes}
                {projectsRoutes}
                {aiRoutes}
                {posRoutes}
                {reportsRoutes}
                {adminRoutes}
                {documentRoutes}
                {complianceRoutes}
                {eamRoutes}
                {settingsRoutes}

                {/* Catch-all */}
                <Route path="*" element={<Navigate to="/dashboard" replace />} />
              </Route>
            </Route>

            {/* Designer: full-screen, outside sidebar Layout, still protected */}
            <Route element={<ProtectedRoute />}>
              <Route path="/document-platform/designer/:templateId" element={<Designer />} />
            </Route>

            {/* Customer Display Screen: full-screen, no sidebar, still protected */}
            <Route element={<ProtectedRoute permission="pos.sale.view" />}>
              <Route path="/pos/customer-display" element={<CustomerDisplayScreen />} />
            </Route>
          </Routes>
        </Suspense>
      </CurrencyProvider>
      {/* AI Chat Widget & Command Palette — only rendered for authenticated users */}
      {isAuthenticated && (
        <>
          <AIChatWidget />
          <CommandPalette />
        </>
      )}
      {/* Session Expired Dialog — global, event-driven, no hard redirect */}
      <SessionExpiredDialog />
    </>
  );
};

const StyledMaterialDesignContent = styled(MaterialDesignContent)(() => ({
  '&.notistack-MuiContent-success': {
    backgroundColor: '#9FE870',
    color: '#163300',
    fontWeight: 600,
  },
  '&.notistack-MuiContent-error': {
    backgroundColor: '#E53935',
    color: '#FFFFFF',
    fontWeight: 600,
  },
  '&.notistack-MuiContent-info': {
    backgroundColor: '#163300',
    color: '#FFFFFF',
    fontWeight: 600,
  },
}));

const CustomErrorSnackbar = React.forwardRef((props, ref) => {
  if (props.message === 'IGNORE_SILENT_RESTRICTION') {
    return null;
  }
  return <StyledMaterialDesignContent ref={ref} {...props} />;
});

function AppContent() {
  const { mode } = useThemeMode();
  const themeOptions = React.useMemo(() => getThemeOptions(mode), [mode]);

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <LanguageProvider baseTheme={themeOptions}>
        <SnackbarProvider
          maxSnack={3}
          anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
          action={(snackbarId) => (
            <IconButton onClick={() => closeSnackbar(snackbarId)} size="small" sx={{ color: 'inherit' }}>
              <CloseIcon fontSize="small" />
            </IconButton>
          )}
          Components={{
            success: StyledMaterialDesignContent,
            error: CustomErrorSnackbar,
            info: StyledMaterialDesignContent,
          }}
        >
          <AuthProvider>
            <FeatureFlagProvider>
              <SubscriptionRestrictionProvider>
                <AppRoutes />
              </SubscriptionRestrictionProvider>
            </FeatureFlagProvider>
          </AuthProvider>
        </SnackbarProvider>
      </LanguageProvider>
    </LocalizationProvider>
  );
}

function App() {
  return (
    <HelmetProvider>
      <ErrorBoundary>
        <QueryClientProvider client={queryClient}>
          <ThemeModeProvider>
            <RegionProvider>
              <AppContent />
            </RegionProvider>
          </ThemeModeProvider>
        </QueryClientProvider>
      </ErrorBoundary>
    </HelmetProvider>
  );
}

export default App;
